import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Gift,
    Plus,
    Trash2,
    RefreshCw,
    Search,
    CheckCircle2,
    XCircle,
    Clock,
    Upload,
    Download,
    Loader2,
    Users,
    AlertCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface PromoEmail {
    id: string;
    email: string;
    credits: number;
    title: string;
    is_claimed: boolean;
    claimed_by: string | null;
    claimed_at: string | null;
    created_at: string;
}

const PromoEmailManagement = () => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(true);
    const [promoEmails, setPromoEmails] = useState<PromoEmail[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);

    // Add form state
    const [newEmail, setNewEmail] = useState("");
    const [newCredits, setNewCredits] = useState("6000");
    const [newTitle, setNewTitle] = useState("Ai faceless Credits (Admin)");
    const [bulkEmails, setBulkEmails] = useState("");
    const [addMode, setAddMode] = useState<"single" | "bulk">("single");
    const [submitting, setSubmitting] = useState(false);

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        claimed: 0,
        unclaimed: 0,
        totalCredits: 0
    });

    const fetchPromoEmails = async () => {
        setLoading(true);
        try {
            const { data, error } = await (supabase as any)
                .from("promo_emails")
                .select("*")
                .order("created_at", { ascending: false });

            if (error) throw error;
            setPromoEmails(data || []);

            // Calculate stats
            const total = data?.length || 0;
            const claimed = data?.filter((e: PromoEmail) => e.is_claimed).length || 0;
            const unclaimed = total - claimed;
            const totalCredits = data?.reduce((sum: number, e: PromoEmail) => sum + e.credits, 0) || 0;

            setStats({ total, claimed, unclaimed, totalCredits });
        } catch (error: any) {
            console.error("Error fetching promo emails:", error);
            toast({
                title: "Error",
                description: "Failed to load promo emails",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPromoEmails();
    }, []);

    // Filter emails based on search
    const filteredEmails = promoEmails.filter(email =>
        email.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.title.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Add single email
    const handleAddSingle = async () => {
        if (!newEmail.trim()) {
            toast({
                title: "Error",
                description: "Please enter an email address",
                variant: "destructive",
            });
            return;
        }

        setSubmitting(true);
        try {
            const { error } = await (supabase as any)
                .from("promo_emails")
                .insert({
                    email: newEmail.trim().toLowerCase(),
                    credits: parseInt(newCredits) || 6000,
                    title: newTitle.trim() || "Ai faceless Credits (Admin)"
                });

            if (error) {
                if (error.code === "23505") {
                    throw new Error("Email already exists in promo list");
                }
                throw error;
            }

            toast({
                title: "Success",
                description: `Added ${newEmail} with ${newCredits} credits`,
            });

            setNewEmail("");
            fetchPromoEmails();
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to add email",
                variant: "destructive",
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Add bulk emails
    const handleAddBulk = async () => {
        const emails = bulkEmails
            .split(/[\n,;]/)
            .map(e => e.trim().toLowerCase())
            .filter(e => e && e.includes("@"));

        if (emails.length === 0) {
            toast({
                title: "Error",
                description: "No valid emails found",
                variant: "destructive",
            });
            return;
        }

        setSubmitting(true);
        try {
            const insertData = emails.map(email => ({
                email,
                credits: parseInt(newCredits) || 6000,
                title: newTitle.trim() || "Ai faceless Credits (Admin)"
            }));

            const { error } = await (supabase as any)
                .from("promo_emails")
                .insert(insertData);

            if (error) throw error;

            toast({
                title: "Success",
                description: `Added ${emails.length} emails successfully`,
            });

            setBulkEmails("");
            fetchPromoEmails();
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to add emails",
                variant: "destructive",
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Delete promo email
    const handleDelete = async (id: string, email: string) => {
        if (!confirm(`Are you sure you want to delete ${email}?`)) return;

        try {
            const { error } = await (supabase as any)
                .from("promo_emails")
                .delete()
                .eq("id", id);

            if (error) throw error;

            toast({ title: "Deleted", description: `${email} removed from promo list` });
            fetchPromoEmails();
        } catch (error: any) {
            toast({
                title: "Error",
                description: "Failed to delete email",
                variant: "destructive",
            });
        }
    };

    // Export to CSV
    const handleExport = () => {
        const csvContent = [
            ["Email", "Credits", "Title", "Status", "Claimed At", "Created At"].join(","),
            ...promoEmails.map(e => [
                e.email,
                e.credits,
                `"${e.title}"`,
                e.is_claimed ? "Claimed" : "Unclaimed",
                e.claimed_at ? new Date(e.claimed_at).toISOString() : "",
                new Date(e.created_at).toISOString()
            ].join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `promo_emails_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card variant="glass">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-500/10">
                                <Users className="h-5 w-5 text-blue-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Promos</p>
                                <p className="text-2xl font-bold">{stats.total}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card variant="glass">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-500/10">
                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Claimed</p>
                                <p className="text-2xl font-bold text-green-500">{stats.claimed}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card variant="glass">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-500/10">
                                <Clock className="h-5 w-5 text-amber-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Unclaimed</p>
                                <p className="text-2xl font-bold text-amber-500">{stats.unclaimed}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card variant="glass">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Gift className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Credits</p>
                                <p className="text-2xl font-bold">{stats.totalCredits.toLocaleString()}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Main Card */}
            <Card variant="glass">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Gift className="h-5 w-5 text-primary" />
                            Promo Email Management
                        </CardTitle>
                        <CardDescription>Manage promotional credit emails for users</CardDescription>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={handleExport}>
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                        </Button>
                        <Button variant="outline" size="sm" onClick={fetchPromoEmails} disabled={loading}>
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                        <Button onClick={() => setShowAddForm(!showAddForm)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Promo
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Add Form */}
                    {showAddForm && (
                        <Card className="bg-muted/30 border-dashed">
                            <CardContent className="p-4 space-y-4">
                                <Tabs value={addMode} onValueChange={(v) => setAddMode(v as "single" | "bulk")}>
                                    <TabsList>
                                        <TabsTrigger value="single">Single Email</TabsTrigger>
                                        <TabsTrigger value="bulk">Bulk Import</TabsTrigger>
                                    </TabsList>

                                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                                        <div className="space-y-2">
                                            <Label>Credits Amount</Label>
                                            <Input
                                                type="number"
                                                value={newCredits}
                                                onChange={(e) => setNewCredits(e.target.value)}
                                                placeholder="6000"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Title/Description</Label>
                                            <Input
                                                value={newTitle}
                                                onChange={(e) => setNewTitle(e.target.value)}
                                                placeholder="Ai faceless Credits (Admin)"
                                            />
                                        </div>
                                    </div>

                                    <TabsContent value="single" className="mt-4 space-y-4">
                                        <div className="space-y-2">
                                            <Label>Email Address</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    value={newEmail}
                                                    onChange={(e) => setNewEmail(e.target.value)}
                                                    placeholder="user@example.com"
                                                    type="email"
                                                />
                                                <Button onClick={handleAddSingle} disabled={submitting}>
                                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                                    <span className="ml-2">Add</span>
                                                </Button>
                                            </div>
                                        </div>
                                    </TabsContent>

                                    <TabsContent value="bulk" className="mt-4 space-y-4">
                                        <div className="space-y-2">
                                            <Label>Email Addresses (one per line or comma-separated)</Label>
                                            <Textarea
                                                value={bulkEmails}
                                                onChange={(e) => setBulkEmails(e.target.value)}
                                                placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                                                rows={6}
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                {bulkEmails.split(/[\n,;]/).filter(e => e.trim() && e.includes("@")).length} valid emails detected
                                            </p>
                                        </div>
                                        <Button onClick={handleAddBulk} disabled={submitting} className="w-full">
                                            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                                            Import All Emails
                                        </Button>
                                    </TabsContent>
                                </Tabs>
                            </CardContent>
                        </Card>
                    )}

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search emails..."
                            className="pl-9"
                        />
                    </div>

                    {/* Email List */}
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : filteredEmails.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Gift className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p>No promo emails found</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {/* Header */}
                            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                <div className="col-span-4">Email</div>
                                <div className="col-span-2">Credits</div>
                                <div className="col-span-2">Status</div>
                                <div className="col-span-3">Date</div>
                                <div className="col-span-1"></div>
                            </div>

                            {/* Rows */}
                            <div className="space-y-1 max-h-[500px] overflow-y-auto">
                                {filteredEmails.map((promo) => (
                                    <div
                                        key={promo.id}
                                        className={`grid grid-cols-12 gap-2 px-3 py-2.5 rounded-lg items-center ${promo.is_claimed ? "bg-green-500/5" : "bg-muted/30"
                                            } hover:bg-muted/50 transition-colors`}
                                    >
                                        <div className="col-span-4 truncate font-mono text-sm">
                                            {promo.email}
                                        </div>
                                        <div className="col-span-2">
                                            <Badge variant="outline" className="font-mono">
                                                {promo.credits.toLocaleString()}
                                            </Badge>
                                        </div>
                                        <div className="col-span-2">
                                            {promo.is_claimed ? (
                                                <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                                    Claimed
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                                                    <Clock className="h-3 w-3 mr-1" />
                                                    Pending
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="col-span-3 text-xs text-muted-foreground">
                                            {promo.is_claimed && promo.claimed_at ? (
                                                <span>Claimed: {new Date(promo.claimed_at).toLocaleDateString("th-TH")}</span>
                                            ) : (
                                                <span>Added: {new Date(promo.created_at).toLocaleDateString("th-TH")}</span>
                                            )}
                                        </div>
                                        <div className="col-span-1 flex justify-end">
                                            {!promo.is_claimed && (
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => handleDelete(promo.id, promo.email)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default PromoEmailManagement;
